import { InspectionGroupApi } from "apiClient/v2/inspection/inspectionGroupApi";
import { ApiResponse } from "interfaces/models/api";
import {
  GetInspectionGroupList,
  InspectionGroup,
} from "interfaces/models/inspectionGroup";
import networkMonitor from "libs/network-monitor/NetworkMonitor";
import { sleep } from "utils/common";
import { PinService } from "./pin";
import CacheService from "libs/cache-project/classes/CacheService";

const handleCachedInspectionGroups = async (
  groups: InspectionGroup[],
  cacheService?: CacheService
) => {
  let requests: Promise<any>[] = [];
  for (let i = 0; i < groups.length; i++) {
    if (cacheService?.isCancel()) throw new Error();
    const group = groups?.[i];
    if (!group?.id) {
      continue;
    }

    requests.push(InspectionGroupApi.getInspectionGroupById(group.id, true));
    requests.push(
      PinService.getAllPins(
        { inspectionGroupId: group.id, shouldCache: true },
        cacheService
      )
    );

    if (requests.length >= 20) {
      await Promise.all(requests);
      await sleep(50);
      requests = [];
    }
  }

  await Promise.all(requests);
};

const getInspectionGroupList = async (
  params?: GetInspectionGroupList,
  cacheService?: CacheService
) => {
  const data = await InspectionGroupApi.getInspectionGroupList(params);

  if (params?.shouldCache) {
    await handleCachedInspectionGroups(data.data, cacheService);
  }

  return data;
};

const getInspectionGroupById = async (id: string, shouldCache?: boolean) => {
  return InspectionGroupApi.getInspectionGroupById(id, shouldCache);
};

const createInspectionGroup = async (inspectionGroup: InspectionGroup) => {
  const response: ApiResponse<InspectionGroup> =
    await InspectionGroupApi.createInspectionGroup(inspectionGroup);
  const isOnline = networkMonitor.isOnline();
  if (!isOnline) {
    await PinService.getAllPins({
      inspectionGroupId: response.data.id,
      shouldCache: true,
    });
  }

  return response;
};

const updateInspectionGroup = async (inspectionGroup: InspectionGroup) => {
  return InspectionGroupApi.updateInspectionGroup(inspectionGroup);
};

const deleteInspectionGroup = async (ids: string[]) => {
  return InspectionGroupApi.deleteInspectionGroup(ids);
};

export const InspectionGroupService = {
  getInspectionGroupList,
  getInspectionGroupById,
  createInspectionGroup,
  updateInspectionGroup,
  deleteInspectionGroup,
};
